import type { ReactNode } from "react";
import { AJ_INTRO, REHEARSAL_SCENE, type CoachOpening, type CoachThreadMessage, type EvidenceReference, type ExperienceLevel, type LearnerProfile, type ProfileEvidence } from "@pushback-and-chaos/contracts";
import { HOME } from "../design/copy.js";
import { Pill } from "../ui/primitives.js";
import { EvidenceLinks } from "../ui/evidence-links.js";
import type { PageItem } from "./storyteller/pages.js";
import type { OnboardingState } from "./home.js";
import { LinkMark } from "./desk-link-mark.js";
import { recommendationParagraph, type LinkedFile } from "./desk-links.js";

/**
 * THE CONVERSATION WITH THE COACH, as atoms on pages.
 *
 * The same move the play surface made with the story (storyteller/narration.tsx):
 * everything the panel shows is flattened into ATOMS — a paragraph the coach
 * said, a line the learner said, a quiet aside, a control — and `splitPages`
 * (storyteller/pages.tsx) decides where the pages fall. The whole conversation
 * stays reachable; it reads as a book whose pages scroll, and the coach's
 * portrait sits at the head of every page.
 *
 * WHERE A PAGE MAY BEGIN (2026-10-02): at an EXCHANGE — a line the learner
 * said, or the coach's word on arrival — and never between a question and its
 * answer. A NEW DAY always begins a page: the conversation reads as a diary,
 * and coming back tomorrow opens on tomorrow's page. Within a day a page turns
 * only once it holds a chapter's worth. A paragraph being typed carries the
 * weight of the WHOLE reply from its first character, so a page is never
 * declared full part-way through an answer.
 *
 * Every string here comes from exactly where it came from before: this file
 * changes where the words sit, never what they say. The one thing it adds is
 * the typed prefix — the newest thing the coach said is rendered as far as it
 * has been revealed (ui/typewriter.ts), and the atom's text IS that prefix.
 *
 * THE INTAKE IS THE CONVERSATION (AJ intake, 2026-09-24). A new learner meets
 * the coach's script, typed; from their first answer on, every line of the
 * intake is kept by the server and read back here like any other line, so a
 * reload reads the same. The two controls the intake owns, the four levels as
 * pills and the read-back with its confirmation, follow the newest line.
 */

export interface CoachAtomsInput {
  readonly opening: CoachOpening | null;
  readonly openingFailed: boolean;
  readonly firstVisit: boolean;
  readonly gated: boolean;
  readonly onboarding: OnboardingState | null;
  readonly thread: readonly CoachThreadMessage[];
  readonly pendingText: string | null;
  readonly busy: boolean;
  readonly error: string | null;
  /**
   * The newest thing the coach said, as far as it has been typed. The atom
   * it belongs to is named by `typingKey`; everything else renders whole.
   */
  readonly typingKey: string | null;
  readonly typed: string;
  /** The clock, for the day a line being worked belongs to. Defaults to now; the tests set it. */
  readonly now?: string | undefined;
  readonly onAnswerLevel: (level: ExperienceLevel) => void;
  readonly onConfirmProfile: () => void;
  readonly onRedoProfile: () => void;
  readonly onSkipRehearsal: () => void;
  readonly onBeginAfterRehearsal: () => void;
  /** "View the moment" (C05): opens the debrief at a moment the coach pointed at. Absent where the surface cannot navigate. */
  readonly onViewMoment?: ((reference: EvidenceReference) => void) | undefined;
  /**
   * THE FILES A REPLY PUT ON THE DESK (2026-10-31, desk-links.ts), by the
   * reply's id. A marker lands at the end of the paragraph that names the
   * case, once the words are all there, with the focus the push was made
   * for said beside it; pressing it selects the connection (home.tsx).
   * Absent where there is no desk to connect to.
   */
  readonly links?: ReadonlyMap<string, LinkedFile> | undefined;
  /** The reply whose connection is open now, if any. */
  readonly selectedLink?: string | null | undefined;
  readonly onSelectLink?: ((messageId: string) => void) | undefined;
}

/** The coach's introduction, as the one utterance that types on the mount that opens on it. */
export const INTRO_KEY = "aj-intro";

/**
 * What the coach is saying now, and under what key — the one utterance the
 * typewriter reveals. The newest thing said wins: the introduction before a
 * new learner has answered it; otherwise the opening if it was written after
 * the last kept line, else the last kept line when it is the coach's.
 */
export function coachUtterance(input: { opening: CoachOpening | null; thread: readonly CoachThreadMessage[]; onboarding: OnboardingState | null }): { key: string; text: string } | null {
  if (input.onboarding?.stage === "intake" && input.thread.length === 0) return { key: INTRO_KEY, text: AJ_INTRO.join(PARAGRAPH_BREAK) };
  const last = input.thread.at(-1);
  const opening = input.opening;
  if (opening !== null && input.onboarding === null && (last === undefined || opening.generatedAt > last.at)) {
    // The welcome is written as paragraphs and typed as one thing; the joint is
    // the blank line between them, so the reveal crosses paragraphs in order.
    return { key: openingKeyOf(opening), text: opening.welcome.join(PARAGRAPH_BREAK) };
  }
  if (last !== undefined) return last.from === "coach" ? { key: last.id, text: last.text } : null;
  return null;
}

const PARAGRAPH_BREAK = "\n\n";
/** Three stars, centred by its style: the seam between two beats of the coach's. */
export const DINKUS = "* * *";
const openingKeyOf = (opening: CoachOpening): string => `opening:${opening.generatedAt}`;

export function coachAtoms(input: CoachAtomsInput): readonly PageItem[] {
  const atoms: PageItem[] = [];
  const push = (item: PageItem): void => {
    atoms.push(item);
  };
  /*
   * THE DINKUS (Andre, 2026-09-25). A beat is one thing said: the learner's line,
   * or a run of the coach's words (a kept message, the introduction, the word on
   * arrival, the rehearsal's aside). When a beat of the coach's follows another
   * with nothing of the learner's between them, a divider marks the seam; a
   * control or a read-back under a beat belongs to it and gets none.
   */
  let lastBeat: "coach" | "you" | null = null;
  const beat = (who: "coach" | "you", key: string): void => {
    if (who === "coach" && lastBeat === "coach") push({ key: `${key}:dinkus`, kind: "text", text: DINKUS, className: "pcs-dinkus" });
    lastBeat = who;
  };
  /*
   * A NEW DAY BEGINS A PAGE. Decided from the kept lines' own timestamps, in
   * the reader's own calendar, so it is the same answer on every visit; the
   * line being worked is placed by the clock, which is what its timestamp will
   * be once it is kept.
   */
  let lastDay: string | null = null;
  const newDay = (at: string | undefined): boolean => {
    if (at === undefined) return false;
    const day = new Date(at).toDateString();
    const changed = lastDay !== null && day !== lastDay;
    lastDay = day;
    return changed;
  };
  const said = (key: string, text: string, at?: string): void => {
    const breakBefore = newDay(at);
    beat("you", key);
    push({ key, kind: "text", text, className: "pcs-said", anchor: true, breakBefore });
  };
  // The coach's prose carries no markup; the one mark the script uses for
  // emphasis is dropped here rather than drawn, since the measured column
  // renders one text node (storyteller/measure.tsx).
  const spoke = (key: string, text: string): void => {
    const plain = text.replaceAll("*", "");
    if (plain.length > 0) push({ key, kind: "text", text: plain, className: "pcs-coach" });
  };
  const aside = (key: string, text: string): void => {
    push({ key, kind: "text", text, className: "pcs-aside" });
  };
  /**
   * The coach's words as paragraphs, the typed prefix where this is what is
   * being typed. `opens` marks the coach speaking FIRST — the introduction,
   * the word on arrival — which begins an exchange and may begin a page; a
   * reply belongs to the line it answers and may not. The first paragraph
   * carries the whole reply's weight, typed or not (pages.tsx, `weight`).
   */
  const spokeParagraphs = (key: string, whole: string, opens: { readonly at: string | undefined } | null = null, mark: { readonly paragraph: number; readonly node: ReactNode } | null = null): boolean => {
    const breakBefore = opens === null ? false : newDay(opens.at);
    beat("coach", key);
    const typing = input.typingKey === key;
    const text = typing ? input.typed : whole;
    const landed = !typing || input.typed === whole;
    for (const [i, paragraph] of paragraphsOf(text).entries()) {
      const plain = paragraph.replaceAll("*", "");
      if (plain.length === 0) continue;
      push({
        key: `${key}:${String(i)}`,
        kind: "text",
        text: plain,
        className: "pcs-coach",
        ...(i === 0 ? { weight: whole.length, anchor: opens !== null, breakBefore } : {}),
        // The marker lands with the last character of the reply, not ahead of the sentence that earned it.
        ...(mark !== null && landed && i === mark.paragraph ? { tail: mark.node } : {}),
      });
    }
    return landed;
  };

  /** The marker for a reply that put a file on the desk, at the end of the paragraph naming the case. */
  const linkMark = (line: CoachThreadMessage): { readonly paragraph: number; readonly node: ReactNode } | null => {
    const file = input.links?.get(line.id);
    const onSelect = input.onSelectLink;
    if (file === undefined || onSelect === undefined) return null;
    const focusWord = file.forFocus ? HOME.linkForFocus : file.forEarlierFocus ? HOME.linkForEarlierFocus : null;
    return {
      paragraph: recommendationParagraph(paragraphsOf(line.text), file.title),
      node: (
        <span className="pcs-link-tail" data-link-tail={line.id}>
          <LinkMark end={`thread:${line.id}`} word={HOME.linkOnDesk} hint={HOME.linkOnDeskHint(file.title)} selected={input.selectedLink === line.id} tone={file.forFocus ? "focus" : "plain"} onSelect={() => { onSelect(line.id); }} />
          {focusWord === null ? null : <span className="pcs-link-focus-word">{focusWord}</span>}
        </span>
      ),
    };
  };

  const { opening, onboarding, gated, busy } = input;

  if (gated) {
    spoke("gated", HOME.gated);
    return atoms;
  }

  /*
   * THE INTRODUCTION: before a new learner has said anything, the coach's
   * script is the whole conversation, typed paragraph by paragraph. From their
   * first answer on it is the first kept line, read back with the rest.
   */
  const intake = onboarding !== null && onboarding.stage === "intake";
  if (intake && input.thread.length === 0) spokeParagraphs(INTRO_KEY, AJ_INTRO.join(PARAGRAPH_BREAK), { at: undefined });

  if (onboarding === null && opening === null) {
    if (input.openingFailed) aside("unavailable", HOME.unavailable);
    else push(workingAtom("waiting", input.firstVisit ? HOME.readingFirst : HOME.reading));
  }

  /*
   * THE WELCOME, in its place in time: after the lines said before it was
   * written and before the ones said since, so a learner whose intake is kept
   * reads the coach's word on arrival where the coach said it.
   */
  let welcomed = onboarding !== null || opening === null;
  const sayWelcome = (): void => {
    if (welcomed || opening === null) return;
    welcomed = true;
    spokeParagraphs(openingKeyOf(opening), opening.welcome.join(PARAGRAPH_BREAK), { at: opening.generatedAt });
  };

  for (const line of input.thread) {
    if (!welcomed && opening !== null && line.at > opening.generatedAt) sayWelcome();
    if (line.from === "you") {
      said(line.id, line.text, line.at);
      continue;
    }
    const landed = spokeParagraphs(line.id, line.text, null, linkMark(line));
    // THE MOMENTS THIS LINE POINTED AT (C05), once the words are all there:
    // a control lands with the last character, not ahead of the sentence
    // that earned it.
    const references = line.references ?? [];
    if (references.length > 0 && landed) {
      push({ key: `${line.id}:refs`, kind: "block", node: () => <EvidenceLinks references={references} onView={input.onViewMoment} /> });
    }
  }
  sayWelcome();

  if (input.pendingText !== null) said("pending", input.pendingText, input.now ?? new Date().toISOString());

  if (busy) {
    push(workingAtom("working", onboarding === null ? HOME.reading : onboarding.stage === "rehearsal" ? HOME.rehearsalReading : HOME.readingYou));
  }

  /*
   * THE INTAKE'S TWO CONTROLS, after the coach's newest words: the four levels
   * as pills that answer without the model, and the draft read back in the
   * learner's own words, confirmed, corrected, or started over before it is
   * kept (Law 6).
   */
  if (intake && !busy) {
    const question = onboarding.question;
    if (question?.field === "experience_level") {
      const options = question.options ?? [];
      push({
        key: "level-pills",
        kind: "block",
        node: () => (
          <div data-testid="level-pills" className="mb-6 flex flex-wrap gap-4">
            {options.map((level) => (
              <Pill key={level} onClick={() => { input.onAnswerLevel(level); }} disabled={busy}>
                {HOME.levelPills[level]}
              </Pill>
            ))}
          </div>
        ),
      });
    }
    const draft = onboarding.draft;
    if (draft !== null) {
      const evidence = onboarding.evidence;
      push({
        key: "read-back",
        kind: "block",
        node: () => (
          <div data-testid="profile-read-back">
            {profileReadBack(draft, evidence).map((line) => (
              <p key={line.slice(0, 40)} className="pcs-coach">
                {line}
              </p>
            ))}
          </div>
        ),
      });
      push({
        key: "confirm",
        kind: "block",
        node: () => (
          <div className="mb-6 flex gap-6">
            <Pill onClick={input.onConfirmProfile} disabled={busy}>
              {HOME.confirmProfile}
            </Pill>
            <Pill onClick={input.onRedoProfile} disabled={busy}>
              {HOME.redoProfile}
            </Pill>
          </div>
        ),
      });
    }
  }

  /*
   * THE REHEARSAL (idea 9): the coach's last aside before the first case, once
   * the papers are on the desk. One move, one reflection, then "begin."
   */
  if (onboarding !== null && onboarding.stage === "rehearsal") {
    beat("coach", "reh");
    for (const [i, paragraph] of HOME.rehearsalIntro.entries()) spoke(`reh:${String(i)}`, paragraph);
    push({ key: "exhibit", kind: "block", node: () => <Exhibit /> });
    if (onboarding.rehearsal === null) {
      spoke("reh-ask", HOME.rehearsalAsk);
      push({ key: "reh-scene", kind: "text", text: REHEARSAL_SCENE, className: "pcs-coach pcs-quoted" });
      spoke("reh-question", HOME.rehearsalQuestion);
      if (!busy) {
        push({
          key: "reh-skip",
          kind: "block",
          node: () => (
            <div className="mb-6 flex gap-6">
              <Pill onClick={input.onSkipRehearsal} disabled={busy}>
                {HOME.rehearsalSkip}
              </Pill>
            </div>
          ),
        });
      }
    } else {
      for (const [i, line] of onboarding.exchanges.entries()) said(`m:${String(i)}`, line.text);
      beat("coach", "l");
      for (const [i, line] of onboarding.rehearsal.lines.entries()) spoke(`l:${String(i)}`, line);
      aside("reh-done", HOME.rehearsalDone);
      push({
        key: "reh-begin",
        kind: "block",
        node: () => (
          <div className="mb-6 flex gap-6">
            <Pill onClick={input.onBeginAfterRehearsal} disabled={busy}>
              {HOME.rehearsalBegin}
            </Pill>
          </div>
        ),
      });
    }
  }

  if (!busy && input.error !== null) {
    push({
      key: "reply-failed",
      kind: "block",
      node: () => (
        <p data-testid="coach-error" className="pcs-aside">
          {HOME.replyFailed}
        </p>
      ),
    });
  }

  return atoms;
}

/** Never a bare spinner: a sentence, pulsing. */
function workingAtom(key: string, text: string): PageItem {
  return {
    key,
    kind: "block",
    node: () => (
      <p className="pcs-aside">
        <span className="inline-block animate-pulse">{text}</span>
      </p>
    ),
  };
}

/** A typed prefix split back into the paragraphs it was joined from. */
function paragraphsOf(text: string): readonly string[] {
  return text.split(PARAGRAPH_BREAK).filter((paragraph) => paragraph.length > 0);
}

/** The atom key of the paragraph in a reply that recommends the named case: where "why this case?" takes the book. */
export function recommendationKey(line: CoachThreadMessage, title: string): string {
  return `${line.id}:${String(recommendationParagraph(paragraphsOf(line.text), title))}`;
}

/**
 * THE EXHIBIT (onboarding pass, idea 6): the same move said two ways, and
 * what each gets. Two examples teach the shape faster than any instruction.
 */
function Exhibit(): ReactNode {
  const x = HOME.exhibit;
  return (
    // Its own formatting context, like the quoted scene: a bordered box beside
    // the portrait is narrowed as a whole, not drawn under the picture.
    <div data-testid="exhibit" className="mb-8 flow-root border border-grid-line-major">
      <p className="m-0 border-b border-b-grid-line-major px-4 py-2 font-display text-micro font-bold text-ink-soft">{HOME.exhibitTitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="border-b border-b-grid-line-major px-4 py-3 sm:border-r sm:border-b-0 sm:border-r-grid-line-major">
          <p className="m-0 mb-2 font-display text-micro font-bold text-ink-soft">{x.terseLabel}</p>
          <p className="m-0 mb-3 font-sans text-small text-ink-soft">“{x.terse}”</p>
          <p className="m-0 font-sans text-small">{x.terseResult}</p>
        </div>
        <div className="px-4 py-3">
          <p className="m-0 mb-2 font-display text-micro font-bold text-ink-soft">{x.narratedLabel}</p>
          <p className="m-0 mb-3 font-sans text-small text-ink-soft">“{x.narrated}”</p>
          <p className="m-0 font-sans text-small">{x.narratedResult}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * The draft read back as sentences in the learner's own words: it was given
 * as prose, and a field grid would invite the learner to think they picked
 * any of it. Where the server verified a quote, the quote is shown, so a
 * learner sees exactly which of their words each sentence rests on; where
 * the approaches are a first guess, the sentence says so. The coach's own
 * lead into it is the newest line of the conversation.
 */
/** What the read-back speaks of: the profile's fields without the row's ids, which the arrival book carries too (2026-10-11). */
export type ReadBackProfile = Pick<LearnerProfile, "name" | "experience_level" | "target_functions" | "target_firms" | "self_identified_gaps" | "approaches" | "approaches_provisional">;

export function profileReadBack(profile: ReadBackProfile, evidence: readonly ProfileEvidence[] = []): readonly string[] {
  const words = (token: string): string => token.replaceAll("_", " ").toLowerCase();
  // A quote that only repeats the value (a level picked from the pills, a
  // name typed after the rounds ran out) adds nothing: show the value alone.
  const quoted = (field: ProfileEvidence["field"], value: string, shown: string = value): string => {
    const found = evidence.find((e) => e.field === field && e.value === value);
    if (found === undefined) return shown;
    const q = found.quote.trim().toLowerCase();
    return q === value.toLowerCase() || q === shown.toLowerCase() ? shown : `${shown} (“${found.quote}”)`;
  };
  // A list whose items all rest on one quote shows that quote once, after
  // the list: "turnaround cases, operations cases (“I want to practice
  // turnaround and operations cases”)" rather than the quote twice over.
  const quotedList = (field: ProfileEvidence["field"], values: readonly string[]): string => {
    const quotes = values.map((v) => evidence.find((e) => e.field === field && e.value === v)?.quote ?? null);
    const first = quotes[0];
    if (values.length > 1 && first !== null && first !== undefined && quotes.every((q) => q === first)) return `${values.join(", ")} (“${first}”)`;
    return values.map((v) => quoted(field, v)).join(", ");
  };
  const lines = [`You are ${profile.name}, ${quoted("experience_level", profile.experience_level, words(profile.experience_level))}.`];
  if (profile.target_functions.length > 0) lines.push(`You want to practice ${quotedList("target_functions", profile.target_functions)}.`);
  if (profile.target_firms.length > 0) lines.push(`You are aiming at ${quotedList("target_firms", profile.target_firms)}.`);
  // A gap is the learner's own phrasing, usually a verb phrase ("stop folding
  // when a client pushes back"), so it is quoted whole rather than spliced
  // into a sentence that reads "work on stop folding".
  if (profile.self_identified_gaps.length > 0) {
    const said = profile.self_identified_gaps.map((v) => `“${evidence.find((e) => e.field === "self_identified_gaps" && e.value === v)?.quote ?? v}”`);
    lines.push(`You said you want to work on this: ${said.join("; ")}.`);
  }
  // THE FIRST FOCUS (AJ intake, 2026-09-24): the one skill they said they would
  // work on first, in the words that showed it, since it becomes a paper on the desk.
  const focus = evidence.find((e) => e.field === "focus_skill");
  if (focus !== undefined) lines.push(`First, you want to work on ${words(focus.value)} (“${focus.quote}”).`);
  if (profile.approaches.length === 0) lines.push(HOME.readBackApproachesNone);
  else if (profile.approaches_provisional) lines.push(HOME.readBackApproachesProvisional.replace("{approaches}", profile.approaches.map(words).join(" and ")));
  else lines.push(`You tend to work ${profile.approaches.map(words).join(" and ")}. ${HOME.readBackApproaches}`);
  return lines;
}
